import React, { useEffect, useState } from "react";
import { FiMessageCircle } from "react-icons/fi";
import { CiPhone } from "react-icons/ci";
import { BiX } from "react-icons/bi";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { DeliverySummary } from "./types";
import axios from "axios";
import { useAppSelector } from "../../redux/hooks";
import { formatNumber } from "../../redux/thunk";

interface DeliverySummaryModalProps {
  trackingId: string;
  onClose: () => void;
}

interface ProgressStepProps {
  label: string;
  date: string;
  isCompleted: boolean;
  isCurrent: boolean;
}

const ProgressStep: React.FC<ProgressStepProps> = ({
  label,
  date,
  isCompleted,
  isCurrent,
}) => (
  <div className="relative flex flex-col items-center w-20 md:w-24 text-center">
    <div
      className={`w-6 h-6 rounded-full ${
        isCompleted ? "bg-[#008B50]" : "bg-gray-300"
      } flex items-center justify-center z-10 border-2 ${
        isCompleted ? "border-white" : "border-transparent"
      }`}
    >
      {isCompleted && <div className="w-2 h-2 bg-white rounded-full"></div>}
    </div>
    <p
      className={`mt-1.5 text-xs font-medium ${
        isCurrent || isCompleted ? "text-[#008B50]" : "text-gray-500"
      }`}
    >
      {label}
    </p>
    {date && (
      <p className="text-[10px] text-gray-400 mt-0.5">
        {new Date(date).toLocaleDateString()}
      </p>
    )}
  </div>
);

export default function DeliverySummaryModal({
  trackingId,
  onClose,
}: DeliverySummaryModalProps) {
  const [deliverySummary, setDeliverySummary] =
    useState<DeliverySummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { token } = useAppSelector((store) => store.auth);

  useEffect(() => {
    const loadDeliverySummary = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/sales/orders/track?orderNumber=${trackingId}`,
          {
            headers: {
              Authorization: token || "",
            },
          }
        );
        setDeliverySummary(response.data.data);
      } catch (error) {
        console.error("Failed to fetch delivery summary:", error);
        setError("Could not load delivery details.");
      } finally {
        setLoading(false);
      }
    };

    loadDeliverySummary();
  }, [trackingId]);

  const steps = ["pending", "approved", "delivered", "cancelled"] as const;

  const getProgressState = (status: (typeof steps)[number]) => {
    const currentStepIndex = steps.indexOf(status);
    return { currentStepIndex };
  };

  const progressData = deliverySummary
    ? getProgressState(deliverySummary.status as (typeof steps)[number])
    : { currentStepIndex: -1 };
  const progressLineWidthPercent =
    progressData.currentStepIndex > 0
      ? (progressData.currentStepIndex / (steps.length - 1)) * 100
      : 0;

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg p-8 max-w-sm w-full text-center shadow-xl">
          <AiOutlineLoading3Quarters className="animate-spin h-10 w-10 text-[#008B50] mx-auto" />
          <p className="mt-4 text-gray-600">Loading Delivery Details...</p>
        </div>
      </div>
    );
  }

  if (error || !deliverySummary) {
    return (
      <div
        className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <div
          className="bg-white rounded-lg p-6 max-w-md w-full relative shadow-xl"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={onClose}
            className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition"
            aria-label="Close modal"
          >
            <BiX size={24} />
          </button>
          <h2 className="text-xl font-semibold text-center mb-4">
            Delivery Summary
          </h2>
          <p className="text-center py-6 text-gray-500">
            {error || "No delivery information found for this tracking ID."}
          </p>
        </div>
      </div>
    );
  }

  // --- Main Modal Content ---
  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl p-6 md:p-8 max-w-xl w-full relative my-auto max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition z-20"
          aria-label="Close modal"
        >
          <BiX size={26} />
        </button>

        <div className="mb-8 pt-4">
          <div className="relative flex justify-between w-full px-1">
            <div className="absolute top-[11px] left-[calc(12.5%+12px)] right-[calc(12.5%+12px)] h-[3px] bg-gray-200" />
            <div
              className="absolute top-[11px] left-[calc(12.5%+12px)] h-[3px] bg-[#008B50] transition-all duration-500 ease-out"
              style={{ width: `${progressLineWidthPercent}%` }}
            />

            {/* Render each step */}
            <ProgressStep
              label="Pending"
              date={
                progressData.currentStepIndex >= 0
                  ? deliverySummary.deliveryDateOption
                  : ""
              }
              isCompleted={progressData.currentStepIndex >= 0}
              isCurrent={progressData.currentStepIndex === 0}
            />
            <ProgressStep
              label="Approved"
              date={
                progressData.currentStepIndex >= 1
                  ? deliverySummary.updatedAt
                  : ""
              }
              isCompleted={progressData.currentStepIndex >= 1}
              isCurrent={progressData.currentStepIndex === 1}
            />
            <ProgressStep
              label="Sent out"
              date={
                progressData.currentStepIndex >= 2
                  ? deliverySummary.updatedAt
                  : ""
              }
              isCompleted={progressData.currentStepIndex >= 2}
              isCurrent={progressData.currentStepIndex === 2}
            />
            <ProgressStep
              label="Delivered"
              date={
                progressData.currentStepIndex >= 3
                  ? deliverySummary.updatedAt
                  : ""
              }
              isCompleted={progressData.currentStepIndex >= 3}
              isCurrent={progressData.currentStepIndex === 3}
            />
          </div>
        </div>

        {/* --- Delivery Address --- */}
        <div className="mb-6 md:mb-8">
          <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-2">
            Delivery
          </h3>
          <p className="text-xs text-gray-500 mb-1">Address</p>
          {/* Changed address text color slightly */}
          <p className="text-sm text-gray-700 leading-snug">
            {deliverySummary.deliveryDetails},
          </p>
        </div>

        {/* --- Contact Buttons --- */}
        <div className="flex justify-center space-x-4 mb-6 md:mb-8">
          {/* Added titles for accessibility */}
          <button
            title="Message Driver"
            className="w-11 h-11 bg-[#008B50] rounded-full flex items-center justify-center text-white shadow hover:bg-[#007A48] transition duration-150 ease-in-out"
          >
            <FiMessageCircle size={20} />
          </button>
          <button
            title="Call Driver"
            className="w-11 h-11 bg-[#008B50] rounded-full flex items-center justify-center text-white shadow hover:bg-[#007A48] transition duration-150 ease-in-out"
          >
            <CiPhone size={22} />
          </button>
          <button
            title="Cancel Order"
            className="w-11 h-11 bg-red-500 rounded-full flex items-center justify-center text-white shadow hover:bg-red-600 transition duration-150 ease-in-out"
          >
            <BiX size={24} />
          </button>
        </div>

        {/* --- Order Summary --- */}
        <div>
          <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-3">
            Order Summary
          </h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Subtotal</span>
              {/* Displaying Subtotal as it is in the data, comment out if not needed */}
              <span className="font-medium text-gray-800">
                N{" "}
                {formatNumber(
                  deliverySummary.orderItems.reduce(
                    (sum, item) => sum + item.totalPrice,
                    0
                  )
                )}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Delivery</span>
              <span className="font-medium text-gray-800">
                N {formatNumber(deliverySummary.deliveryFee)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Service Fee</span>
              <span className="font-medium text-gray-800">
                + N {formatNumber(deliverySummary.serviceFee)}
              </span>
            </div>
            <div className="border-t border-gray-200 pt-3 mt-3">
              <div className="flex justify-between font-bold text-base text-gray-900">
                <span>Total</span>
                <span>
                  N {formatNumber(deliverySummary.allItemsTotalPrice)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
